"use server";

import {
  getBCProducstBySkus,
  getCustomerDataByIds,
  getOrderByNumberForActualCustomers,
  getOrderLinesByOrderNumber,
  getQuoteByNumberForActualCustomers,
  getQuoteLinesByQuoteNumber,
} from "@/lib/business-central";
import {
  createSession,
  getSession,
  markSessionLogged,
} from "@/lib/customer-area-session";
import {
  getPermisoForSection,
  hasWriteAccess,
} from "@/lib/customer-area-permissions";
import { getLocale, getText } from "@/lib/locale";
import { genericEmail } from "@/lib/mails";
import { createProductsExcelFile } from "@/lib/products-excel";
import {
  loginSchema,
  registerCompleteSchema,
  recoverPasswordSchema,
  resetPasswordSchema,
  changePasswordSchema,
  contactEmailSchema,
} from "@/lib/schemas/login";
import {
  acceptPolicies,
  forgotPassword,
  getMe,
  logLogin,
  resetPassword,
  strapiLogin,
  strapiRegister,
  STRAPI_ME_CACHE_TAG,
} from "@/lib/strapi";
import { randomBytes } from "crypto";
import { redirect } from "next/navigation";
import { revalidateTag } from "next/cache";
import z from "zod";

export async function loginAction(prevState: any, formData: FormData) {
  const email = formData.get("email") as string;
  const pass = formData.get("pass") as string;

  const result = loginSchema.safeParse({
    email,
    pass,
  });

  if (!result.success) {
    return { errors: z.treeifyError(result.error).properties };
  }

  const data = await strapiLogin(email, pass);

  if (data.error || data?.user?.active === false) {
    return { errors: { general: "errors.login" } };
  }

  await createSession(data.jwt);
  await markSessionLogged();
  await logLogin(data.jwt);

  redirect(
    (await getLocale()) !== "es-es"
      ? `/${await getLocale()}/customer-area`
      : "/customer-area",
  );
}

export async function recoverPasswordAction(
  prevState: any,
  formData: FormData,
) {
  const email = formData.get("email") as string;
  const result = recoverPasswordSchema.partial().safeParse({ email });
  if (!result.success) {
    return { errors: z.treeifyError(result.error).properties };
  }

  await forgotPassword(email);

  return { success: true };
}

export async function resetPasswordAction(prevState: any, formData: FormData) {
  const pass = formData.get("pass") as string;
  const confirmPass = formData.get("confirmPass") as string;

  const result = resetPasswordSchema.safeParse({
    pass,
    confirmPass,
  });

  if (!result.success) {
    return { errors: z.treeifyError(result.error).properties };
  }

  const data = await resetPassword(
    pass,
    confirmPass,
    formData.get("code") as string,
  );

  if (data.error) {
    return { errors: { general: "errors.resetPassword" } };
  }

  try {
    await genericEmail(
      data.user.email,
      await getText("customer.mail.passwordChangeConfirmSubject"),
      false,
      null,
      null,
      null,
      `${(
        await getText("customer.mail.passwordChangeConfirmGreeting")
      ).replace("[name]", data.user.name)}<br/><br/>
      ${await getText("customer.mail.passwordChangeConfirmBody")}<br/><br/>
      ${await getText("customer.mail.passwordChangeConfirmNotRequested")}`,
      true,
    );
  } catch (e) {
    console.log(e);
  }

  return { success: true };
}

export async function changePasswordAction(prevState: any, formData: FormData) {
  const userData = await getMe();
  if (
    !userData ||
    !hasWriteAccess(getPermisoForSection("user-data", userData.permisos))
  ) {
    return { errors: { general: "errors.register" } };
  }

  const currentPassword = formData.get("currentPassword") as string;
  const pass = formData.get("pass") as string;
  const confirmPass = formData.get("confirmPass") as string;

  const result = changePasswordSchema.safeParse({
    currentPassword,
    pass,
    confirmPass,
  });

  if (!result.success) {
    return { errors: z.treeifyError(result.error).properties };
  }

  const data = await resetPassword(
    pass,
    confirmPass,
    undefined,
    currentPassword,
  );

  if (data.error) {
    console.log(data);
    return { errors: { general: data.error } };
  }

  try {
    await genericEmail(
      data.user.email,
      await getText("customer.mail.passwordChangeConfirmSubject"),
      false,
      null,
      null,
      null,
      `${(
        await getText("customer.mail.passwordChangeConfirmGreeting")
      ).replace("[name]", data.user.name)}<br/><br/>
      ${await getText("customer.mail.passwordChangeConfirmBody")}<br/><br/>
      ${await getText("customer.mail.passwordChangeConfirmNotRequested")}`,
      true,
    );
  } catch (e) {
    console.log(e);
  }

  return { success: true };
}

export async function registerCompleteAction(
  prevState: any,
  formData: FormData,
) {
  const data = {
    email: formData.get("email") as string,
    name: formData.get("name") as string,
    surname: formData.get("surname") as string,
    companyName: formData.get("companyName") as string,
    customerCode: ((formData.get("customerCode") as string) ?? "").trim(),
    companyCIF: ((formData.get("companyCIF") as string) ?? "").trim(),
    position: formData.get("position") as string,
    phone: formData.get("phone") as string,
    authorization: formData.get("authorization") === "on",
  };

  const result = registerCompleteSchema.safeParse(data);

  if (!result.success) {
    return { errors: z.treeifyError(result.error).properties };
  }

  const username = `${data.name} ${data.surname}`.trim() || data.email;

  // El usuario no elige contraseña al registrarse: Strapi exige una, así que
  // se genera una aleatoria que nadie conoce. La crea el propio usuario desde
  // el email que recibe cuando un administrador lo activa.
  const response = await strapiRegister({
    username,
    email: data.email,
    password: randomBytes(32).toString("base64url"),
    confirmed: false,
    name: data.name,
    surname: data.surname,
    companyName: data.companyName,
    customerCode: data.customerCode,
    companyCIF: data.companyCIF,
    position: data.position,
    phone: data.phone,
    isClient: true,
    language: await getLocale(),
  });

  if ((response as any)?.error) {
    return { errors: { general: "errors.register" } };
  }

  try {
    await genericEmail(
      "rgarzon@pukkas.com,fobiols@pukkas.com,vgasol@casagay.com",
      "Nova sol·licitud d'accés " + data.email,
      false,
      null,
      {
        email: data.email,
        name: data.name,
        surname: data.surname,
      },
      {
        Empresa: data.companyName,
        "Codi de client": data.customerCode,
        CIF: data.companyCIF,
        Càrrec: data.position,
        Telèfon: data.phone,
      },
      `L'usuari ${data.name} ${data.surname} (${data.email}) ha sol·licitat accés al portal de clients.<br/>Pots validar la seva sol·licitud a l'apartat d'usuaris del gestor de continguts (Strapi).`,
      false,
      "ca",
    );
  } catch (e) {
    console.log(e);
    return { errors: { general: "errors.register" } };
  }

  try {
    await genericEmail(
      data.email,
      await getText("customer.mail.accessRequestReceivedSubject"),
      false,
      null,
      null,
      null,
      `${(
        await getText("customer.mail.accessRequestReceivedGreeting")
      ).replace("[name]", data.name)}<br/><br/>
      ${await getText("customer.mail.accessRequestReceivedBody")}<br/><br/>
      ${await getText("customer.mail.accessRequestReceivedFooter")}`,
      true,
    );
  } catch (e) {
    console.log(e);
  }

  const locale = await getLocale();
  redirect((locale !== "es-es" ? `/${locale}` : "") + "/success-registered");
}

export async function updateDataAction(prevState: any, formData: FormData) {
  const to = formData.get("to") as string;
  const subject = formData.get("subject") as string;
  const message = formData.get("message") as string;
  const files = formData.getAll("file") as File[] | null;
  const clientId = formData.get("client") as string;
  const userData = await getMe();

  const result = contactEmailSchema.safeParse({
    to,
    subject,
    message,
  });

  if (!result.success) {
    return { errors: z.treeifyError(result.error).properties };
  }

  const customerData = await getCustomerDataByIds([clientId]);

  if (!customerData[0]?.["@odata.etag"]) {
    return { errors: { general: "errors.register" } };
  }

  try {
    await genericEmail(
      "rgarzon@pukkas.com,fobiols@pukkas.com,vgasol@casagay.com",
      subject,
      message,
      files,
      userData,
      customerData[0],
      undefined,
      false,
      "ca",
    );
  } catch (e) {
    console.log(e);
    return { errors: { general: "errors.register" } };
  }

  try {
    await genericEmail(
      userData.email,
      await getText("customer.mail.updateDataRequestSubject"),
      false,
      null,
      null,
      null,
      `${(
        await getText("customer.mail.updateDataRequestGreeting")
      ).replace("[name]", userData.name)}<br/><br/>
      ${await getText("customer.mail.updateDataRequestBody")}<br/><br/>
      ${await getText("customer.mail.updateDataRequestFooter")}`,
      true,
    );
  } catch (e) {
    console.log(e);
  }

  return { success: true, errors: false };
}

export async function repeatOrderAction(prevState: any, formData: FormData) {
  const locale = await getLocale();
  const message = formData.get("message") as string;
  const files = formData.getAll("file") as File[] | null;
  const userData = await getMe();
  const orderNumber = formData.get("orderNumber") as string;

  const order = await getOrderByNumberForActualCustomers(orderNumber);

  if (!order) {
    return { errors: { general: "errors.orderNotFound" } };
  }

  const customerData = await getCustomerDataByIds([order.customerId]);
  const orderLines = await getOrderLinesByOrderNumber(order.number);

  const productsExcel = await createProductsExcelFile(
    orderLines.map((line: any) => ({
      sku: line.lineObjectNumber,
      quantity: line.quantity,
      name: line.description,
    })),
    customerData[0].number,
  );

  try {
    await genericEmail(
      "rgarzon@pukkas.com,fobiols@pukkas.com,vgasol@casagay.com",
      "Repetir comanda " + order.number,
      message,
      [...(files || []), productsExcel],
      userData,
      customerData[0],
      `L'usuari ${userData.name} ${userData.surname} (${
        userData.email
      }) ha sol·licitat repetir la comanda número ${order.number}.<br/><br/>
      <p style='margin-bottom:6px'><strong><u>Productes</u></strong></p>
      ${orderLines
        .map(
          (line: any) =>
            `- ${line.description} (SKU: ${line.lineObjectNumber}, Quantitat: ${line.quantity})`,
        )
        .join("<br/>")}
      `,
      false,
      "ca",
    );
  } catch (e) {
    console.log(e);
    return { errors: { general: "errors.register" } };
  }

  try {
    await genericEmail(
      userData.email,
      (
        await getText("customer.mail.orderRepeatConfirmSubject")
      ).replace("[no.]", order.number),
      false,
      null,
      null,
      null,
      `${(
        await getText("customer.mail.orderRepeatConfirmGreeting")
      ).replace("[name]", userData.name)}<br/><br/>
      ${(await getText("customer.mail.orderRepeatConfirmBody"))
        .replace("[no.]", order.number)
        .replace("[amount]", order.totalAmountIncludingTax)}<br/><br/>
      ${await getText("customer.mail.orderRepeatConfirmFooter")}`,
      true,
    );
  } catch (e) {
    console.log(e);
  }

  redirect(
    (locale !== "es-es" ? `/${locale}` : "") +
      "/customer-area/orders/repeat-success",
  );
}

export async function newOrderAction(prevState: any, formData: FormData) {
  const locale = await getLocale();
  const message = formData.get("message") as string;
  const files = formData.getAll("file") as File[] | null;

  const customerId = formData.get("customerId") as string;
  const qtities = JSON.parse(formData.get("qtities") as string);

  const userData = await getMe();
  if (!userData) {
    return { errors: { general: "errors.register" } };
  }

  //Check if customerId is one of the customers of the user
  const customerData = await getCustomerDataByIds([customerId]);
  if (
    !customerData[0] ||
    !userData.bcCustomerData.some(
      (c: any) => c.number === customerData[0].number,
    )
  ) {
    return { errors: { general: "errors.register" } };
  }

  const productInfo = await getBCProducstBySkus(
    Object.keys(qtities).map((key) => key.split("quantity-")[1]),
  );

  const productsExcel = await createProductsExcelFile(
    Object.entries(qtities).map(([key, value]) => {
      const sku = key.split("quantity-")[1];
      const product = productInfo.find((p: any) => p.number === sku);
      return {
        sku,
        quantity: value as number,
        name: product?.displayName || "Producte no trobat",
      };
    }),
    customerData[0].number,
  );

  try {
    await genericEmail(
      "rgarzon@pukkas.com,fobiols@pukkas.com,vgasol@casagay.com",
      "Nova comanda del client " + customerData[0].displayName,
      message,
      [...(files || []), productsExcel],
      userData,
      customerData[0],
      `L'usuari ${userData.name} ${userData.surname} (${
        userData.email
      }) ha sol·licitat una nova comanda per al client ${
        customerData[0].displayName
      }.<br/><br/>
      <p style='margin-bottom:6px'><strong><u>Productes</u></strong></p>
      ${Object.entries(qtities)
        .map(([key, value]) => {
          const sku = key.split("quantity-")[1];
          const product = productInfo.find((p: any) => p.number === sku);
          return `- ${
            product?.displayName || "Producte no trobat"
          } (SKU: ${sku}, Quantitat: ${value})`;
        })
        .join("<br/>")}
      `,
      false,
      "ca",
    );
  } catch (e) {
    console.log(e);
    return { errors: { general: "errors.register" } };
  }

  try {
    await genericEmail(
      userData.email,
      await getText("customer.mail.orderRequestConfirmSubject"),
      false,
      null,
      null,
      null,
      `${(
        await getText("customer.mail.orderRequestConfirmGreeting")
      ).replace("[name]", userData.name)}<br/><br/>
      ${await getText("customer.mail.orderRequestConfirmBody")}<br/><br/>
      ${await getText("customer.mail.orderRequestConfirmFooter")}`,
      true,
    );
  } catch (e) {
    console.log(e);
  }

  redirect(
    (locale !== "es-es" ? `/${locale}` : "") +
      "/customer-area/orders/new-success",
  );
}

export async function validateQuoteAction(prevState: any, formData: FormData) {
  const locale = await getLocale();
  const message = formData.get("message") as string;
  const files = formData.getAll("file") as File[] | null;
  const userData = await getMe();
  const quoteNumber = formData.get("orderNumber") as string;

  const quote = await getQuoteByNumberForActualCustomers(quoteNumber);

  if (!quote) {
    return { errors: { general: "errors.quoteNotFound" } };
  }

  const customerData = await getCustomerDataByIds([quote.customerId]);
  const orderLines = await getQuoteLinesByQuoteNumber(quote.number);

  const productsExcel = await createProductsExcelFile(
    orderLines.map((line: any) => ({
      sku: line.lineObjectNumber,
      quantity: line.quantity,
      name: line.description,
    })),
    customerData[0].number,
  );

  try {
    await genericEmail(
      "rgarzon@pukkas.com,fobiols@pukkas.com,vgasol@casagay.com",
      "Acceptar pressupost " + quote.number,
      message,
      [...(files || []), productsExcel],
      userData,
      customerData[0],
      `L'usuari ${userData.name} ${userData.surname} (${
        userData.email
      }) ha sol·licitat acceptar el pressupost número ${quote.number}.<br/><br/>
      <p style='margin-bottom:6px'><strong><u>Productes</u></strong></p>
      ${orderLines
        .map(
          (line: any) =>
            `- ${line.description} (SKU: ${line.lineObjectNumber}, Quantitat: ${line.quantity})`,
        )
        .join("<br/>")}
      `,
      false,
      "ca",
    );
  } catch (e) {
    console.log(e);
    return { errors: { general: "errors.register" } };
  }

  try {
    await genericEmail(
      userData.email,
      (
        await getText("customer.mail.quoteAcceptanceConfirmSubject")
      ).replace("[no.]", quote.number),
      false,
      null,
      null,
      null,
      `${(
        await getText("customer.mail.quoteAcceptanceConfirmGreeting")
      ).replace("[name]", userData.name)}<br/><br/>
      ${(await getText("customer.mail.quoteAcceptanceConfirmBody"))
        .replace("[no.]", quote.number)
        .replace("[amount]", quote.totalAmountIncludingTax)}<br/><br/>
      ${await getText("customer.mail.quoteAcceptanceConfirmFooter")}`,
      true,
    );
  } catch (e) {
    console.log(e);
  }

  redirect(
    (locale !== "es-es" ? `/${locale}` : "") +
      "/customer-area/quotes/validate-success",
  );
}

export async function newQuoteAction(prevState: any, formData: FormData) {
  const locale = await getLocale();
  const message = formData.get("message") as string;
  const files = formData.getAll("file") as File[] | null;

  const customerId = formData.get("customerId") as string;
  const qtities = JSON.parse(formData.get("qtities") as string);

  const userData = await getMe();
  if (!userData) {
    return { errors: { general: "errors.register" } };
  }

  //Check if customerId is one of the customers of the user
  const customerData = await getCustomerDataByIds([customerId]);
  if (
    !customerData[0] ||
    !userData.bcCustomerData.some(
      (c: any) => c.number === customerData[0].number,
    )
  ) {
    return { errors: { general: "errors.register" } };
  }

  const productInfo = await getBCProducstBySkus(
    Object.keys(qtities).map((key) => key.split("quantity-")[1]),
  );

  const productsExcel = await createProductsExcelFile(
    Object.entries(qtities).map(([key, value]) => {
      const sku = key.split("quantity-")[1];
      const product = productInfo.find((p: any) => p.number === sku);
      return {
        sku,
        quantity: value as number,
        name: product?.displayName || "Producte no trobat",
      };
    }),
    customerData[0].number,
  );

  try {
    await genericEmail(
      "rgarzon@pukkas.com,fobiols@pukkas.com,vgasol@casagay.com",
      "Nova sol·licitud de pressupost del client " +
        customerData[0].displayName,
      message,
      [...(files || []), productsExcel],
      userData,
      customerData[0],
      `L'usuari ${userData.name} ${userData.surname} (${
        userData.email
      }) ha sol·licitat una nova cotització per al client ${
        customerData[0].displayName
      }.<br/><br/>
      <p style='margin-bottom:6px'><strong><u>Productes</u></strong></p>
      ${Object.entries(qtities)
        .map(([key, value]) => {
          const sku = key.split("quantity-")[1];
          const product = productInfo.find((p: any) => p.number === sku);
          return `- ${
            product?.displayName || "Producte no trobat"
          } (SKU: ${sku}, Quantitat: ${value})`;
        })
        .join("<br/>")}
      `,
      false,
      "ca",
    );
  } catch (e) {
    console.log(e);
    return { errors: { general: "errors.register" } };
  }

  try {
    await genericEmail(
      userData.email,
      await getText("customer.mail.quoteRequestConfirmSubject"),
      false,
      null,
      null,
      null,
      `${(
        await getText("customer.mail.quoteRequestConfirmGreeting")
      ).replace("[name]", userData.name)}<br/><br/>
      ${await getText("customer.mail.quoteRequestConfirmBody")}<br/><br/>
      ${await getText("customer.mail.quoteRequestConfirmFooter")}`,
      true,
    );
  } catch (e) {
    console.log(e);
  }

  redirect(
    (locale !== "es-es" ? `/${locale}` : "") +
      "/customer-area/quotes/new-success",
  );
}

export async function acceptPoliciesAction() {
  const session = await getSession();
  if (!session || !(await acceptPolicies(session))) {
    return { success: false };
  }

  // /api/users/me se cachea 20 minutos: sin esto el pop-up volvería a salir.
  revalidateTag(STRAPI_ME_CACHE_TAG);
  return { success: true };
}
